/**
 * Two-factor sign-in UI — ONE implementation for every surface: the builder's
 * login page and Settings, the Run view, published sites and the downloaded
 * runtime (roadmap item 1, phase 1).
 *
 * The components know nothing about endpoints: a host passes the calls in.
 * The server draws the QR code (`qr_svg`, an SVG data URI) so no surface
 * needs a QR library; when it is missing the key is shown to type instead.
 *
 * Colours read the host's CSS variables with the shell palette as fallback,
 * so the builder's light/dark themes and the runtime both look native.
 */
import { useEffect, useRef, useState } from 'react'
import type { CSSProperties, FormEvent } from 'react'
import { Check, Copy, Download, KeyRound, Loader2, Smartphone } from 'lucide-react'

export interface MfaSetupStart {
  secret: string
  otpauth_uri: string
  qr_svg?: string | null
  issuer?: string
  account?: string
}

// Each colour reads an MFA-specific variable first, then the host theme. A
// container that is always light (the shell's white modals) sets the
// --tw-mfa-* variables — MFA_LIGHT_VARS — because the host theme may be dark.
const c = {
  text: 'var(--tw-mfa-text, var(--color-text, #0f172a))',
  muted: 'var(--tw-mfa-muted, var(--color-text-secondary, #64748b))',
  border: 'var(--tw-mfa-border, var(--color-border, #e2e8f0))',
  bg: 'var(--tw-mfa-bg, var(--color-bg, #fff))',
  subtle: 'var(--tw-mfa-subtle, var(--color-bg-secondary, #f8fafc))',
  accent: 'var(--tw-mfa-accent, var(--color-accent, #4f46e5))',
  error: 'var(--tw-mfa-error, var(--color-error, #dc2626))',
}

/** Spread onto a wrapper whose background is always white. */
export const MFA_LIGHT_VARS = {
  '--tw-mfa-text': '#0f172a',
  '--tw-mfa-muted': '#64748b',
  '--tw-mfa-border': '#e2e8f0',
  '--tw-mfa-bg': '#fff',
  '--tw-mfa-subtle': '#f8fafc',
  '--tw-mfa-accent': '#4f46e5',
  '--tw-mfa-error': '#dc2626',
} as CSSProperties

const input: CSSProperties = {
  padding: '0.55rem 0.7rem', border: `1px solid ${c.border}`, borderRadius: 8,
  fontSize: '1rem', width: '100%', boxSizing: 'border-box', color: c.text, background: c.bg,
}

function btn(kind: 'primary' | 'ghost' = 'primary'): CSSProperties {
  const base: CSSProperties = {
    padding: '0.55rem 0.95rem', borderRadius: 8, fontWeight: 600, fontSize: '0.9375rem',
    cursor: 'pointer', border: '1px solid transparent', display: 'inline-flex',
    alignItems: 'center', gap: 6, justifyContent: 'center',
  }
  return kind === 'primary'
    ? { ...base, background: c.accent, color: '#fff' }
    : { ...base, background: 'transparent', color: c.muted, borderColor: c.border }
}

function errorText(e: unknown, fallback: string): string {
  const d = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
  if (typeof d === 'string') return d
  if (d && typeof d === 'object') {
    const m = (d as { error?: { message?: string }; message?: string })
    return m.error?.message || m.message || fallback
  }
  return (e as Error)?.message || fallback
}

const Spin = () => <Loader2 size={15} style={{ animation: 'spin 1s linear infinite' }} />

/** Ask for the 6-digit code (or a recovery code) to finish signing in. */
export function MfaCodeForm({ onSubmit, onCancel, title = 'Two-factor sign-in', autoFocus = true }: {
  onSubmit: (code: string) => Promise<void>
  onCancel?: () => void
  title?: string
  autoFocus?: boolean
}) {
  const [code, setCode] = useState('')
  const [useRecovery, setUseRecovery] = useState(false)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const ref = useRef<HTMLInputElement>(null)
  useEffect(() => { if (autoFocus) ref.current?.focus() }, [autoFocus, useRecovery])

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (!code.trim()) return
    setBusy(true); setErr(null)
    try { await onSubmit(code.trim()) } catch (x) {
      setErr(errorText(x, 'That code did not work — try again.'))
      setCode('')
    } finally { setBusy(false) }
  }

  return (
    <form onSubmit={submit} data-tw-mfa="code" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: c.text }}>
        {useRecovery ? <KeyRound size={18} /> : <Smartphone size={18} />}
        <strong style={{ fontSize: '1.0625rem' }}>{title}</strong>
      </div>
      <p style={{ margin: 0, color: c.muted, fontSize: '0.875rem' }}>
        {useRecovery
          ? 'Enter one of the recovery codes you saved when you set up two-factor sign-in. Each code works once.'
          : 'Enter the 6-digit code from your authenticator app.'}
      </p>
      <input
        ref={ref}
        value={code}
        onChange={(e) => setCode(useRecovery ? e.target.value : e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
        inputMode={useRecovery ? 'text' : 'numeric'}
        autoComplete="one-time-code"
        placeholder={useRecovery ? 'xxxxx-xxxxx' : '123456'}
        aria-label={useRecovery ? 'Recovery code' : 'Authentication code'}
        style={{ ...input, letterSpacing: useRecovery ? '0.05em' : '0.35em', textAlign: 'center', fontSize: '1.25rem' }}
      />
      {err && <div role="alert" style={{ color: c.error, fontSize: '0.875rem' }}>{err}</div>}
      <button type="submit" disabled={busy || !code.trim()} style={{ ...btn('primary'), opacity: busy || !code.trim() ? 0.7 : 1 }}>
        {busy && <Spin />} Verify
      </button>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
        <button type="button" onClick={() => { setUseRecovery((v) => !v); setCode(''); setErr(null) }}
          style={{ background: 'none', border: 'none', color: c.accent, cursor: 'pointer', padding: 0, fontSize: '0.875rem' }}>
          {useRecovery ? 'Use the authenticator app instead' : 'Lost your phone? Use a recovery code'}
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel}
            style={{ background: 'none', border: 'none', color: c.muted, cursor: 'pointer', padding: 0, fontSize: '0.875rem' }}>
            Cancel
          </button>
        )}
      </div>
    </form>
  )
}

/** Show the recovery codes ONCE, with copy / download, and require an
 *  explicit "saved them" before moving on. */
export function RecoveryCodesPanel({ codes, onDone, doneLabel = 'Continue', account }: {
  codes: string[]
  onDone: () => void
  doneLabel?: string
  account?: string
}) {
  const [saved, setSaved] = useState(false)
  const [copied, setCopied] = useState(false)
  const text = codes.join('\n')

  const copy = async () => {
    try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000) } catch { /* clipboard blocked: the codes are on screen */ }
  }
  const download = () => {
    const header = `Recovery codes${account ? ` for ${account}` : ''}\nEach code signs you in once if you lose your authenticator.\n\n`
    const url = URL.createObjectURL(new Blob([header + text + '\n'], { type: 'text/plain' }))
    const a = document.createElement('a')
    a.href = url; a.download = 'recovery-codes.txt'; a.click()
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  }

  return (
    <div data-tw-mfa="recovery-codes" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <strong style={{ color: c.text, fontSize: '1.0625rem' }}>Save your recovery codes</strong>
      <p style={{ margin: 0, color: c.muted, fontSize: '0.875rem' }}>
        If you lose your phone, each of these signs you in once. Keep them somewhere safe —
        they will not be shown again.
      </p>
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '6px 16px',
        padding: '12px 14px', border: `1px solid ${c.border}`, borderRadius: 8, background: c.subtle,
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '0.9375rem', color: c.text,
      }}>
        {codes.map((code) => <span key={code}>{code}</span>)}
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button type="button" onClick={copy} style={btn('ghost')}>
          {copied ? <Check size={15} /> : <Copy size={15} />} {copied ? 'Copied' : 'Copy'}
        </button>
        <button type="button" onClick={download} style={btn('ghost')}><Download size={15} /> Download</button>
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: c.text, fontSize: '0.875rem', cursor: 'pointer' }}>
        <input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} style={{ width: 'auto' }} />
        I have saved these codes
      </label>
      <button type="button" disabled={!saved} onClick={onDone} style={{ ...btn('primary'), opacity: saved ? 1 : 0.6 }}>
        {doneLabel}
      </button>
    </div>
  )
}

/** Set up an authenticator app: QR (or key), then a code to confirm, then the
 *  recovery codes. ``start`` / ``confirm`` are the host's calls. */
export function MfaSetup({ start, confirm, onDone, onCancel, intro }: {
  start: () => Promise<MfaSetupStart>
  confirm: (code: string) => Promise<{ recovery_codes: string[] }>
  onDone: (recoveryCodes: string[]) => void
  onCancel?: () => void
  intro?: string
}) {
  const [setup, setSetup] = useState<MfaSetupStart | null>(null)
  const [codes, setCodes] = useState<string[] | null>(null)
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [showKey, setShowKey] = useState(false)
  const began = useRef(false)

  useEffect(() => {
    if (began.current) return
    began.current = true
    start().then((s) => { setSetup(s); if (!s.qr_svg) setShowKey(true) })
      .catch((e) => setErr(errorText(e, 'Could not start the setup — try again.')))
  }, [start])

  if (codes) {
    return <RecoveryCodesPanel codes={codes} account={setup?.account} onDone={() => onDone(codes)} />
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setBusy(true); setErr(null)
    try {
      const res = await confirm(code)
      setCodes(res.recovery_codes)
    } catch (x) {
      setErr(errorText(x, 'That code did not work — try again.'))
      setCode('')
    } finally { setBusy(false) }
  }

  const grouped = (setup?.secret || '').replace(/(.{4})/g, '$1 ').trim()

  return (
    <form onSubmit={submit} data-tw-mfa="setup" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: c.text }}>
        <Smartphone size={18} /><strong style={{ fontSize: '1.0625rem' }}>Set up two-factor sign-in</strong>
      </div>
      <p style={{ margin: 0, color: c.muted, fontSize: '0.875rem' }}>
        {intro || 'Scan this with an authenticator app (Google Authenticator, Microsoft Authenticator, 1Password…), then enter the 6-digit code it shows.'}
      </p>
      {!setup && !err && <div style={{ padding: 24, textAlign: 'center', color: c.muted }}><Spin /></div>}
      {setup && (
        <>
          {setup.qr_svg && (
            <img src={setup.qr_svg} alt="QR code for your authenticator app" width={184} height={184}
              style={{ alignSelf: 'center', background: '#fff', borderRadius: 8, padding: 6, border: `1px solid ${c.border}` }} />
          )}
          {showKey ? (
            <div style={{ fontSize: '0.875rem', color: c.muted }}>
              Or enter this key{setup.account ? ` for ${setup.account}` : ''}:
              <div style={{
                marginTop: 4, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', color: c.text,
                padding: '8px 10px', border: `1px solid ${c.border}`, borderRadius: 8, background: c.subtle,
                wordBreak: 'break-all', userSelect: 'all',
              }}>{grouped}</div>
            </div>
          ) : (
            <button type="button" onClick={() => setShowKey(true)}
              style={{ background: 'none', border: 'none', color: c.accent, cursor: 'pointer', padding: 0, fontSize: '0.875rem', alignSelf: 'center' }}>
              Can’t scan? Enter a key instead
            </button>
          )}
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
            inputMode="numeric" autoComplete="one-time-code" placeholder="123456"
            aria-label="Code from your authenticator app"
            style={{ ...input, letterSpacing: '0.35em', textAlign: 'center', fontSize: '1.25rem' }}
          />
        </>
      )}
      {err && <div role="alert" style={{ color: c.error, fontSize: '0.875rem' }}>{err}</div>}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        {onCancel && <button type="button" onClick={onCancel} style={btn('ghost')}>Cancel</button>}
        <button type="submit" disabled={busy || code.length !== 6}
          style={{ ...btn('primary'), opacity: busy || code.length !== 6 ? 0.7 : 1 }}>
          {busy && <Spin />} Turn on
        </button>
      </div>
    </form>
  )
}

/* ── A password sign-in parked on its second factor ──────────────────── */

/** What every sign-in endpoint (platform, hosted site, downloaded runtime)
 *  answers instead of a session when a second factor is needed. */
export interface ParkedSignIn {
  mfa_required?: boolean
  mfa_enrolment_required?: boolean
  mfa_token?: string
}

export type MfaStep = { kind: 'verify' | 'enrol'; token: string }

/** The step a sign-in answer asks for, or null when it carries a session. */
export function parkedStep(res: ParkedSignIn | null | undefined): MfaStep | null {
  if (!res || !res.mfa_token) return null
  if (res.mfa_required) return { kind: 'verify', token: res.mfa_token }
  if (res.mfa_enrolment_required) return { kind: 'enrol', token: res.mfa_token }
  return null
}

/** The code form or the setup for a parked sign-in. ``api`` is the host's
 *  three calls; ``onSignedIn`` receives the session answer (for setup: once
 *  the recovery codes have been saved). */
export function MfaSignInStep<T>({ step, api, onSignedIn, onCancel }: {
  step: MfaStep
  api: {
    verify: (token: string, code: string) => Promise<T>
    enrolStart: (token: string) => Promise<MfaSetupStart>
    enrolConfirm: (token: string, code: string) => Promise<T & { recovery_codes: string[] }>
  }
  onSignedIn: (session: T) => void
  onCancel?: () => void
}) {
  const done = useRef<T | null>(null)
  if (step.kind === 'verify') {
    return (
      <MfaCodeForm
        onSubmit={async (code) => { onSignedIn(await api.verify(step.token, code)) }}
        onCancel={onCancel}
      />
    )
  }
  return (
    <MfaSetup
      intro="Two-factor sign-in is required for this account. Scan this with an authenticator app (Google Authenticator, Microsoft Authenticator, 1Password…), then enter the 6-digit code it shows."
      start={() => api.enrolStart(step.token)}
      confirm={async (code) => {
        const res = await api.enrolConfirm(step.token, code)
        done.current = res
        return res
      }}
      onDone={() => { if (done.current) onSignedIn(done.current) }}
      onCancel={onCancel}
    />
  )
}

/** Ask for the second factor of a parked sign-in in an overlay of its own,
 *  for surfaces where the sign-in came from a page block rather than a form
 *  the host can swap (a site's `auth_form`, the identify step). Resolves with
 *  the session answer, or null when the person cancels. */
export function promptMfaSignIn<T>(step: MfaStep, api: {
  verify: (token: string, code: string) => Promise<T>
  enrolStart: (token: string) => Promise<MfaSetupStart>
  enrolConfirm: (token: string, code: string) => Promise<T & { recovery_codes: string[] }>
}): Promise<T | null> {
  return new Promise((resolve) => {
    const host = document.createElement('div')
    host.setAttribute('data-tw-mfa-overlay', '')
    document.body.appendChild(host)
    let root: { render: (n: React.ReactNode) => void; unmount: () => void } | null = null
    const close = (value: T | null) => {
      setTimeout(() => { root?.unmount(); host.remove() }, 0)
      resolve(value)
    }
    void import('react-dom/client').then(({ createRoot }) => {
      root = createRoot(host)
      root.render(
        <div role="dialog" aria-modal="true" onClick={() => close(null)} style={{
          position: 'fixed', inset: 0, zIndex: 12000, background: 'rgba(15,23,42,0.45)',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '5rem 1rem',
          overflow: 'auto',
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            ...MFA_LIGHT_VARS,
            width: '100%', maxWidth: 400, background: '#fff', color: '#0f172a', borderRadius: 14,
            boxShadow: '0 20px 60px rgba(0,0,0,0.25)', padding: '1.25rem',
          }}>
            <MfaSignInStep step={step} api={api} onSignedIn={(s) => close(s)} onCancel={() => close(null)} />
          </div>
        </div>,
      )
    })
  })
}

/* ── The signed-in person's own two-factor settings ──────────────────── */

export interface MfaStatusInfo {
  supported?: boolean
  enrolled: boolean
  enrolled_at: string | null
  recovery_codes_remaining: number
  policy: { mode: string; grace_days: number }
  can_disable: boolean
  enrol_by: string | null
}

/** The host's calls for {@link TwoFactorPanel}. Changing an EXISTING factor
 *  (new codes, a new phone, switching off) sends the password AND a code. */
export interface MfaSelfApi {
  status: () => Promise<MfaStatusInfo>
  totpStart: (confirm?: { password: string; code: string }) => Promise<MfaSetupStart>
  totpConfirm: (code: string) => Promise<{ recovery_codes: string[] }>
  recoveryCodes: (password: string, code: string) => Promise<{ recovery_codes: string[] }>
  disable: (password: string, code: string) => Promise<MfaStatusInfo>
}

type PanelMode = 'idle' | 'setup' | 'replace' | 'confirm-codes' | 'confirm-replace' | 'confirm-disable'

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(iso) ? iso : `${iso}Z`)
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString()
}

export function TwoFactorPanel({ api, notify }: {
  api: MfaSelfApi
  notify?: { success: (m: string) => void; error: (m: string) => void }
}) {
  const [status, setStatus] = useState<MfaStatusInfo | null>(null)
  const [mode, setMode] = useState<PanelMode>('idle')
  const [codes, setCodes] = useState<string[] | null>(null)
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const [confirmWith, setConfirmWith] = useState<{ password: string; code: string } | undefined>()
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const load = async () => {
    try { setStatus(await api.status()) } catch (e) { setErr(errorText(e, 'Could not load two-factor settings')) }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void load() }, [])

  const reset = () => { setMode('idle'); setPassword(''); setCode(''); setErr(null); setConfirmWith(undefined) }
  const say = (m: string) => (notify ? notify.success(m) : undefined)

  const confirm = async (e: FormEvent) => {
    e.preventDefault()
    setBusy(true); setErr(null)
    try {
      if (mode === 'confirm-codes') {
        setCodes((await api.recoveryCodes(password, code)).recovery_codes)
        reset()
      } else if (mode === 'confirm-disable') {
        setStatus(await api.disable(password, code))
        say('Two-factor sign-in is off')
        reset()
      } else if (mode === 'confirm-replace') {
        setConfirmWith({ password, code })
        setPassword(''); setCode('')
        setMode('replace')
      }
    } catch (x) {
      setErr(errorText(x, 'That did not work — check your password and code'))
    } finally { setBusy(false) }
  }

  if (!status) return err ? <div style={{ color: c.error, fontSize: '0.875rem' }}>{err}</div> : null
  if (status.supported === false) {
    return (
      <p style={{ margin: 0, color: c.muted, fontSize: '0.875rem' }} data-tw-mfa="unsupported">
        Two-factor sign-in is not available for this application yet.
      </p>
    )
  }
  const required = status.policy.mode === 'required'
  const link: CSSProperties = { ...btn('ghost'), padding: '0.4rem 0.7rem', fontSize: '0.875rem' }

  return (
    <div data-tw-mfa="panel" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {codes ? (
        <RecoveryCodesPanel codes={codes} doneLabel="Done" onDone={() => { setCodes(null); void load() }} />
      ) : mode === 'setup' || mode === 'replace' ? (
        <MfaSetup
          start={() => api.totpStart(mode === 'replace' ? confirmWith : undefined)}
          confirm={api.totpConfirm}
          onDone={() => { reset(); void load(); say('Two-factor sign-in is on') }}
          onCancel={reset}
        />
      ) : mode !== 'idle' ? (
        <form onSubmit={confirm} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <p style={{ margin: 0, color: c.muted, fontSize: '0.875rem' }}>
            {mode === 'confirm-disable'
              ? 'Confirm with your password and a code from your authenticator app to switch it off.'
              : mode === 'confirm-replace'
                ? 'Confirm with your password and a code from your CURRENT authenticator (or a recovery code).'
                : 'Confirm with your password and a code to make new recovery codes. The old ones stop working.'}
          </p>
          <input type="password" placeholder="Password" autoComplete="current-password" style={input}
            value={password} onChange={(e) => setPassword(e.target.value)} />
          <input placeholder="Authenticator or recovery code" autoComplete="one-time-code" style={input}
            value={code} onChange={(e) => setCode(e.target.value)} />
          {err && <div role="alert" style={{ color: c.error, fontSize: '0.875rem' }}>{err}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" onClick={reset} style={btn('ghost')} disabled={busy}>Cancel</button>
            <button type="submit" disabled={busy || !password || !code}
              style={{ ...btn('primary'), opacity: busy || !password || !code ? 0.7 : 1 }}>
              {busy && <Spin />} {mode === 'confirm-disable' ? 'Turn off' : 'Continue'}
            </button>
          </div>
        </form>
      ) : status.enrolled ? (
        <>
          <p style={{ margin: 0, color: c.muted, fontSize: '0.875rem' }}>
            On{fmtDate(status.enrolled_at) ? ` since ${fmtDate(status.enrolled_at)}` : ''} — signing in asks
            for a code from your authenticator app. {status.recovery_codes_remaining} recovery
            code{status.recovery_codes_remaining === 1 ? '' : 's'} left.
          </p>
          {status.recovery_codes_remaining <= 2 && (
            <p style={{ margin: 0, color: c.error, fontSize: '0.875rem' }}>You are running out of recovery codes — make new ones.</p>
          )}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <button type="button" style={link} onClick={() => setMode('confirm-codes')}>New recovery codes</button>
            <button type="button" style={link} onClick={() => setMode('confirm-replace')}>Use a new phone</button>
            {status.can_disable && (
              <button type="button" style={{ ...link, color: c.error }} onClick={() => setMode('confirm-disable')}>Turn off</button>
            )}
          </div>
          {!status.can_disable && (
            <p style={{ margin: 0, color: c.muted, fontSize: '0.8125rem' }}>Two-factor sign-in is required, so it cannot be switched off.</p>
          )}
        </>
      ) : (
        <>
          <p style={{ margin: 0, color: c.muted, fontSize: '0.875rem' }}>
            Add a second step to signing in: a 6-digit code from an authenticator app on your phone.
            {required && status.enrol_by ? ` It is required — set it up before ${fmtDate(status.enrol_by)}.` : ''}
          </p>
          <div><button type="button" style={btn('primary')} onClick={() => setMode('setup')}>Set up two-factor sign-in</button></div>
        </>
      )}
    </div>
  )
}
